import type Mithril from 'mithril';
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import FieldSet from 'flarum/common/components/FieldSet';
import Switch from 'flarum/common/components/Switch';
import Button from 'flarum/common/components/Button';
import sortTags from 'flarum/tags/common/utils/sortTags';

function translate(id: string): string {
	return app.translator.trans(id) as string;
}

/**
 * The `SignupConfirmPage` component displays the user's settings control panel, in
 * the context of their user profile.
 */
export default class SignupConfirmPage extends Page {
	private alertEmailConfirmationHider?: HTMLStyleElement;
	private csrfToken?: string;
	private tagMeta_: { [key: string]: { loading: boolean} } = {};
	private newsletter = { loading: false };
	private vspt_nonprofit = { loading: false };
	private vspt_partner = { loading: false };
	private accepted_terms = false;
	private validate_loading = false;
	
	private tagMeta(tagId: string) {
		if(!this.tagMeta_.hasOwnProperty(tagId))
			this.tagMeta_[tagId] = { loading: false };
		return this.tagMeta_[tagId];
	}
	
	oninit(vnode: Mithril.Vnode) {
		super.oninit(vnode);

		app.setTitle(translate('core.forum.settings.title'));
		
		// this is a hack to prevent the "email sent" alert to appear.
		// since we are already displaying the page that can only be accessed by clicking the email link, it makes no sense to display this alert on this page
		this.alertEmailConfirmationHider = document.createElement('style');
		this.alertEmailConfirmationHider.innerText = '.Alert { display: none; }';
		document.head.append(this.alertEmailConfirmationHider);
		
		this.csrfToken = app.data.session.csrfToken;
	}
	
	onremove() {
		if(this.alertEmailConfirmationHider)
			this.alertEmailConfirmationHider.remove();
	}

	view() {
		const isEmailValidationPage = (app.current.data as any).routeName == 'vspt.signup-confirm';
		return (
			<div className="SignupConfirmPage">
				<div className="container">
					<p>{translate('vspt_signup.forum.confirm_form.message')}</p>
					
					<ul>{listItems(this.settingsItems().toArray())}</ul>
					
					{ isEmailValidationPage
						? <form className="form" method="POST" action="">
							<input type="hidden" name="csrfToken" value={this.csrfToken} />
					        <p className="form-group">
					        	{this.accepted_terms ? [] : <p className="helpText">{translate('vspt_signup.forum.confirm_form.validate_help')}</p>}
					            <button type="submit" className="Button Button--primary" disabled={!this.accepted_terms}>{translate('vspt_signup.forum.confirm_form.validate_button')}</button>
					        </p>
					    </form>
					    : <div>
					    	{this.accepted_terms ? [] : <p className="helpText">{translate('vspt_signup.forum.confirm_form.validate_help')}</p>}
					    	<Button className="Button Button--primary" onclick={() => this.validate()} loading={this.validate_loading} disabled={!this.accepted_terms}>{translate('vspt_signup.forum.confirm_form.validate_button')}</Button>
					    </div>
					}
			    </div>
			</div>
		);
	}
	
	settingsItems() {
		const user = app.session.user;
		
		const items = new ItemList();
		
		if('flarum-tags' in flarum.extensions && 'fof-follow-tags' in flarum.extensions) {
			const tags = sortTags(app.store.all('tags') as any[])
		  		.filter((tag: any) => tag.position() !== null && !tag.isChild())
		  		.filter((tag: any) => tag.expose_in_confirm_form());
      		
      		if(tags.length > 0) {
				items.add('subscribeCategories',
					<FieldSet className="confirm-subscribe-categories">
						<legend>{translate('vspt_signup.forum.confirm_form.subscribe_categories')}</legend>
						<p className="helpText">{translate('vspt_signup.forum.confirm_form.subscribe_categories_help')}</p>
						
						<ul className="SignupConfirmPage-switches">
						{tags.map((tag: any) => (
							<li><Switch loading={this.tagMeta(tag.id()).loading} state={tag.subscription() || false} onchange={(val: boolean) => this.saveTagSubscription(tag, val)}>{tag.name()}</Switch></li>
						))}
						</ul>
					</FieldSet>
			    );
		    }
	    }
	    
	    if(user) {
		    items.add('newsletter',
		    	<FieldSet>
		    		<legend>{translate('vspt_signup.forum.confirm_form.newsletter')}</legend>
		    		<p className="helpText">{translate('vspt_signup.forum.confirm_form.newsletter_help')}</p>
		    		<Switch loading={this.newsletter.loading} state={user.subscribed_newsletter()} onchange={(val: boolean) => this.saveNewsletterSubscription(val)}>{translate('vspt_signup.forum.confirm_form.newsletter_switch')}</Switch>
		    	</FieldSet>
		    );
	    }
	    
	    if('justoverclock-users-map-location' in flarum.extensions) {
			const { AddLocationComponent } = require('@justoverclock-users-map-location/forum');
			
			items.add('location',
				<AddLocationComponent></AddLocationComponent>
			);
		}
		
		items.add('terms',
			<FieldSet>
	    		<legend>{translate('vspt_signup.forum.confirm_form.terms')}</legend>
	    		<ul className="SignupConfirmPage-switches">
	    			<li><Switch state={this.accepted_terms} onchange={(val: boolean) => this.accepted_terms = val}>{translate('vspt_signup.forum.confirm_form.terms_switch')} <a href={translate('vspt_signup.forum.confirm_form.terms_link_url')} target="_blank">{translate('vspt_signup.forum.confirm_form.terms_link_text')}</a></Switch></li>
	    		</ul>
	    	</FieldSet>
		);

		return items;
	}
	
	saveTagSubscription(tag: any, subscription: boolean) {
		if(this.tagMeta(tag.id()).loading)
			return;
		
		let currentState: boolean = tag.subscription() || false;
		if(subscription == currentState) return;
		
		this.tagMeta(tag.id()).loading = true;
		
		app
			.request({
				url: `${app.forum.attribute('apiUrl')}/tags/${tag.id()}/subscription`,
				method: 'POST',
				body: {
					data: {
						subscription: subscription ? 'follow' : false,
					},
				},
			})
    		.then((res: any) => app.store.pushPayload(res))
    		.then(() => {
				this.tagMeta(tag.id()).loading = false;
				m.redraw();
			});
	}
	
	saveNewsletterSubscription(subscribed: boolean) {
		if(this.newsletter.loading)
			return;
		
		const user = app.session.user;
		if(!user) return;
		
		if(user.subscribed_newsletter() == subscribed) return;
		
		this.newsletter.loading = true;
		
		let attributes = {
			subscribed_newsletter: subscribed
		};
		user.save(attributes)
			.then((res: any) => app.store.pushPayload(res))
			.then(() => {
				this.newsletter.loading = false;
				m.redraw();
			});
	}
	
	requestVSPTNonProfitEmail(requestEmailInfo: boolean) {
		if(this.vspt_nonprofit.loading)
			return;
		
		const user = app.session.user;
		if(!user) return;
		
		let alreadyRequested = !!user.vspt_nonprofit_info_email();
		if(alreadyRequested == requestEmailInfo) return;
		
		this.vspt_nonprofit.loading = true;
		
		let attributes = {
			vspt_nonprofit_info_email: requestEmailInfo ? 'requested' : null
		};
		user.save(attributes)
			.then((res: any) => app.store.pushPayload(res))
			.then(() => {
				this.vspt_nonprofit.loading = false;
				m.redraw();
			});
	}
	
	requestVSPTPartnerEmail(requestEmailInfo: boolean) {
		if(this.vspt_partner.loading)
			return;
		
		const user = app.session.user;
		if(!user) return;
		
		let alreadyRequested = !!user.vspt_partner_info_email();
		if(alreadyRequested == requestEmailInfo) return;
		
		this.vspt_partner.loading = true;
		
		let attributes = {
			vspt_partner_info_email: requestEmailInfo ? 'requested' : null
		};
		user.save(attributes)
			.then((res: any) => app.store.pushPayload(res))
			.then(() => {
				this.vspt_partner.loading = false;
				m.redraw();
			});
	}
	
	validate() {
		if(this.validate_loading) return;
		
		const user = app.session.user;
		if(!user) return;
		
		this.validate_loading = true;
		
		let attributes = {
			hasValidatedSignupForm: true
		};
		user.save(attributes)
			.then((res: any) => app.store.pushPayload(res))
			.then(() => {
				const user = app.session.user;
				if(user && user.hasValidatedSignupForm()) {
					// signup form has been validated, we can redirect to the forum index
					(window as any).location = "/";
				}
				else {
					app.alerts.show({ type: 'error' }, translate('vspt_signup.forum.confirm_form.failed_validation'));
					this.validate_loading = false;
					m.redraw();
				}
			});
	}
}
